import React, { useState, useEffect, useCallback } from 'react';

import { ControlOutlined, MoreOutlined } from '@ant-design/icons';
import { Button, Row, Col, Space, Dropdown, Checkbox, Typography, App, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

export default function SubHeader({
  selected,
  onSelectAllRecords,
  columnsEditable=true,
  tableId,
  headerColumns,
  onColumnsChange,
  actions //custom actions
}) {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const buttonStyle = {
    bgColor: selected.size > 0 ? 'btn-warning' : 'btn-success',
  };

  const [toggleColumns, setToggleColumns] = useState(false);
  const [columns, setColumns] = useState(headerColumns);
  const [savedColumns, setSavedColumns] = useState(null);  // 保存開啟面板前的狀態
  const [ selectingAll, setSelectingAll] = useState(false)

  const tableColumnsKey = `gowarehouse-tableColumns-${tableId}`


  const handleSaveColumns = () => {
    // 只保存有 nameKey 或 sortFieldName 的欄位標識符，避免保存 JSX 元素
    const visibleColumns = columns
      .filter(column => column.visible)
      .map(column => column.nameKey || column.sortFieldName || (typeof column.name === 'string' ? column.name : null))
      .filter(Boolean);
    localStorage.setItem(tableColumnsKey, JSON.stringify(visibleColumns));
    notification.success({
      message: t('common.table.save_columns_success'),
      placement: 'topRight',
      duration: 4.5,
    });
    setToggleColumns(false);
    setSavedColumns(null);  // 清除保存的狀態
  }

  const handleCancelColumns = () => {
    // 恢復到開啟面板前的狀態
    if (savedColumns) {
      setColumns(savedColumns);
      onColumnsChange(savedColumns);
    }
    setToggleColumns(false);
    setSavedColumns(null);  // 清除保存的狀態
  }

  const handleToggleColumns = () => {
    if (!toggleColumns) {
      // 開啟面板前，保存當前狀態
      setSavedColumns([...columns]);
    }
    setToggleColumns(!toggleColumns);
  }

  // 獲取欄位的唯一標識符
  const getColumnKey = (column) => {
    return column.nameKey || column.sortFieldName || (typeof column.name === 'string' ? column.name : null);
  };

  const handleLoadColumns = useCallback(() => {
    if (!columnsEditable) {
      // If not editable, ensure all columns are visible
      // Check if we actually need to update to avoid infinite loop
      const allVisible = headerColumns.map(column => ({ ...column, visible: true }));
      const needsUpdate = headerColumns.some(c => !c.visible);

      if (needsUpdate) {
        setColumns(allVisible);
        onColumnsChange(allVisible);
      }
      return;
    }

    const storedVisibleKeys = JSON.parse(localStorage.getItem(tableColumnsKey));
    if (storedVisibleKeys) {
      const restoredColumns = headerColumns.map(column => {
        const columnKey = getColumnKey(column);
        // 如果找不到標識符，預設顯示
        const shouldBeVisible = columnKey ? storedVisibleKeys.includes(columnKey) : true;
        return { ...column, visible: shouldBeVisible };
      });

      // Check if update is needed
      const needsUpdate = headerColumns.some((c, i) => c.visible !== restoredColumns[i].visible);

      if (needsUpdate) {
        setColumns(restoredColumns);
        onColumnsChange(restoredColumns);
      }
    } else {
      // If no stored settings, use all default visible
      const allVisible = headerColumns.map(column => ({ ...column, visible: true }));
      const needsUpdate = headerColumns.some(c => !c.visible);

      if (needsUpdate) {
        setColumns(allVisible);
        onColumnsChange(allVisible);
      }
    }
  }, [columnsEditable, tableId]); // Removed headerColumns and onColumnsChange to prevent loop

  useEffect(() => {
    handleLoadColumns();
  }, [handleLoadColumns]);

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
        {actions && actions.length > 0 ? (
          <>
            <Col>
              <Space size="middle">
                {selected.size > 0 && (
                  <Tag color="blue" style={{ margin: 0, fontSize: '13px', padding: '2px 12px' }}>
                    {t('common.table.selected_records', { count: selected.size })}
                  </Tag>
                )}
                <Button
                  type={selected.size > 0 ? "default" : "primary"}
                  size="small"
                  loading={selectingAll}
                  onClick={async () => {
                    setSelectingAll(true);
                    try {
                      await onSelectAllRecords(selected.size > 0);
                    } catch (error) {
                      console.error('onSelectAllRecords failed:', error);
                    } finally {
                      setSelectingAll(false);
                    }
                  }}
                >
                  {selectingAll
                    ? t('common.table.selecting_records')
                    : selected.size > 0
                    ? t('common.table.clear_selection')
                    : t('common.table.select_all_records')}
                </Button>
              </Space>
            </Col>
            <Col flex="auto">
              <Space wrap style={{ justifyContent: 'flex-end', width: '100%' }}>
                {actions
                  .filter(action => (action.persist || selected.size > 0) && !action.folded)
                  .map((action, index) => (
                    <Button
                      key={index}
                      type={action.type || 'default'}
                      danger={action.danger}
                      size="small"
                      icon={action.icon}
                      onClick={() => {
                        action.do(selected);
                      }}
                    >
                      {t(action.name)}
                    </Button>
                  ))}
                {actions.filter(action => (action.persist || selected.size > 0) && action.folded)
                  .length > 0 && (
                  <Dropdown
                    menu={{
                      items: (() => {
                        const foldedActions = actions.filter(action => (action.persist || selected.size > 0) && action.folded);
                        const hasGroups = foldedActions.some(a => a.group);

                        if (!hasGroups) {
                          // 無 group：維持原行為（平鋪）
                          return foldedActions.map((action, index) => ({
                            key: index,
                            label: t(action.name),
                            icon: action.icon,
                            danger: action.danger,
                            onClick: () => action.do(selected),
                          }));
                        }

                        // 有 group：按 group 分段，保留原始順序
                        const groups = [];
                        const seen = new Set();
                        foldedActions.forEach(action => {
                          const g = action.group || '__ungrouped__';
                          if (!seen.has(g)) {
                            seen.add(g);
                            groups.push(g);
                          }
                        });

                        const items = [];
                        groups.forEach((group, gi) => {
                          if (gi > 0) {
                            items.push({ type: 'divider', key: `divider-${gi}` });
                          }
                          if (group !== '__ungrouped__') {
                            items.push({ type: 'group', label: t(group), key: `group-${gi}`, children:
                              foldedActions
                                .filter(a => (a.group || '__ungrouped__') === group)
                                .map((action, ai) => ({
                                  key: `${gi}-${ai}`,
                                  label: t(action.name),
                                  icon: action.icon,
                                  danger: action.danger,
                                  onClick: () => action.do(selected),
                                }))
                            });
                          } else {
                            foldedActions
                              .filter(a => !a.group)
                              .forEach((action, ai) => {
                                items.push({
                                  key: `ungrouped-${ai}`,
                                  label: t(action.name),
                                  icon: action.icon,
                                  danger: action.danger,
                                  onClick: () => action.do(selected),
                                });
                              });
                          }
                        });
                        return items;
                      })(),
                    }}
                    trigger={['click']}
                  >
                    <Button size="small" icon={<MoreOutlined />}>
                      {t('common.table.more_actions')}
                    </Button>
                  </Dropdown>
                )}
              </Space>
            </Col>
          </>
        ) : (
          <Col span={22}></Col>
        )}
        {columnsEditable && (
          <Col>
            <Button
              type="text"
              icon={<ControlOutlined />}
              onClick={handleToggleColumns}
              title={t('common.table.column_settings_title')}
            />
          </Col>
        )}
      </Row>
      {toggleColumns && (
        <div
          style={{
            position: 'absolute',
            right: '20px',
            top: '50px',
            background: '#fff',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            padding: '16px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            minWidth: '200px'
          }}
        >
          <div style={{ marginBottom: '12px' }}>
            <Text strong>{t('common.table.column_display_settings')}</Text>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {columns.map((column, index) => {
              if (column.persist) return null;
              return (
                <Checkbox
                  key={index}
                  checked={column.visible}
                  onChange={() => {
                    const newColumns = [...columns];
                    newColumns[index] = { ...newColumns[index], visible: !newColumns[index].visible };
                    setColumns(newColumns);
                    onColumnsChange(newColumns);
                  }}
                >
                  {column.name}
                </Checkbox>
              );
            })}
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <Button type="primary" size="small" onClick={handleSaveColumns} style={{ flex: 1 }}>
                {t('common.button.save')}
              </Button>
              <Button size="small" onClick={handleCancelColumns} style={{ flex: 1 }}>
                {t('common.button.cancel')}
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
